// src/pages/LandingPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import hero from '../assets/hero.jpg';
import logoContoso from '../assets/logos/contoso.jpg';
import logoFabrikam from '../assets/logos/fabrikam.jpg';
import logoNorthwind from '../assets/logos/northwind.jpg';
import logoAdventureWorks from '../assets/logos/adventureworks.jpg';
import logoTailwind from '../assets/logos/tailwind.jpg';
import avatarAlex from '../assets/avatars/alex.jpg';
import { trackEvent } from '../analytics';

function Icon({ children }) {
  return <span className="icon" aria-hidden>{children}</span>;
}

function LandingPage() {
  return (
    <div className="landing">
      <header className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="eyebrow">Azure-native | Enterprise-ready</span>
            <h1>Continuous HIPAA compliance for Microsoft Cloud</h1>
            <p className="subtitle">
              Automate auditing, detect violations in real time, and ship auditor-ready reports - without agents or manual spreadsheets.
            </p>
            <div className="cta-row">
              <a href="/.auth/login/aad" className="button button-primary" onClick={() => trackEvent('cta_get_started_landing')}>Get Started</a>
              <Link to="/pricing" className="button button-outline" onClick={() => trackEvent('cta_view_pricing_landing')}>View Pricing</Link>
            </div>
            <div className="trust-row">
              <div className="trust-item">No agents to install</div>
              <div className="dot"/>
              <div className="trust-item">Least-privilege by design</div>
              <div className="dot"/>
              <div className="trust-item">Deployed in minutes</div>
            </div>
          </div>
          <div className="hero-art">
            <img
              className="hero-photo"
              src={hero}
              alt="Analyst reviewing compliance dashboard with charts"
              loading="eager"
              fetchpriority="high"
            />
          </div>
        </div>
      </header>

      <section className="logos" aria-label="Trusted by teams">
        <div className="logos-inner">
          <img className="logo" src={logoContoso} alt="Contoso"/>
          <img className="logo" src={logoFabrikam} alt="Fabrikam"/>
          <img className="logo" src={logoNorthwind} alt="Northwind"/>
          <img className="logo" src={logoAdventureWorks} alt="AdventureWorks"/>
          <img className="logo" src={logoTailwind} alt="Tailwind"/>
        </div>
      </section>

      <main className="landing-main content">
        <section className="how-it-works">
          <h2>Stop Guessing. Start Automating.</h2>
          <p className="section-subtitle">
            Manual compliance checks are complex and error-prone. Our system gives you enterprise-grade protection without the enterprise-level overhead.
          </p>
          <div className="steps-container">
            <div className="step">
              <div className="step-icon">1</div>
              <h4>Connect Your Tenant</h4>
              <p>Grant secure, read-only access to your Azure tenant in our simple 2-minute onboarding flow.</p>
            </div>
            <div className="step">
              <div className="step-icon">2</div>
              <h4>Subscribe</h4>
              <p>Choose our simple, affordable monthly plan. No enterprise contracts, no hidden fees, no sales calls.</p>
            </div>
            <div className="step">
              <div className="step-icon">3</div>
              <h4>Stay Compliant</h4>
              <p>Receive PDF reports and instant email alerts when a new violation is detected. It's that simple.</p>
            </div>
          </div>
        </section>

        <section className="features">
          <h2>Key Features</h2>
          <p className="section-subtitle">Continuous, automated checks with auditor-ready evidence - no agents, no spreadsheets.</p>
          <div className="feature-grid">
            <div className="feature">
              <Icon>*</Icon>
              <h3>Azure & M365 native</h3>
              <p>Connect to Log Analytics and Microsoft 365 signals to continuously evaluate HIPAA controls.</p>
            </div>
            <div className="feature">
              <Icon>*</Icon>
              <h3>Real-time detection</h3>
              <p>Identify risky behavior and configuration drift as it happens with automated alerts.</p>
            </div>
            <div className="feature">
              <Icon>*</Icon>
              <h3>Auditor-ready reports</h3>
              <p>Generate professional PDFs with evidence and remediation guidance in one click.</p>
            </div>
            <div className="feature">
              <Icon>*</Icon>
              <h3>Zero manual spreadsheets</h3>
              <p>Replace ad-hoc checklists with a repeatable, automated compliance engine.</p>
            </div>
            <div className="feature">
              <Icon>*</Icon>
              <h3>Least-privileged setup</h3>
              <p>Read-only permissions and scoped access keep your environment secure by default.</p>
            </div>
            <div className="feature">
              <Icon>*</Icon>
              <h3>Full traceability</h3>
              <p>Every finding includes who, what, when - with links back to native logs.</p>
            </div>
          </div>
        </section>

        <section className="testimonial">
          <div className="quote">
            "Compliance Guardian helped us cut audit prep from weeks to hours. Our security team now focuses on prevention instead of paperwork."
          </div>
          <div className="customer">
            <img className="avatar" src={avatarAlex} alt="Alex Rivera"/>
            <div>
              <div className="name">Alex Rivera</div>
              <div className="role">Head of Security, Meridian Health</div>
            </div>
            <div className="metrics">
              <div><strong>90%</strong><span>faster audits</span></div>
              <div><strong>24/7</strong><span>continuous checks</span></div>
              <div><strong>0</strong><span>agents required</span></div>
            </div>
          </div>
        </section>

        <section className="cta-band">
          <div className="cta-text">
            <h2>Be audit-ready - all the time</h2>
            <p>Spin up continuous HIPAA monitoring in minutes and stay ahead of the next review.</p>
          </div>
          <div className="cta-actions">
            <a href="/.auth/login/aad" className="button button-primary" onClick={() => trackEvent('cta_start_setup_landing_band')}>Start Free Setup</a>
            <Link to="/pricing" className="button button-outline" onClick={() => trackEvent('cta_see_plans_landing_band')}>See Plans</Link>
          </div>
        </section>
      </main>
      <footer className="site-footer" aria-label="Footer">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <a href="https://nxgntools.com/tools/compliance-guardian"
            target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', width: 'auto' }}>
            <img src="https://nxgntools.com/api/embed/compliance-guardian?type=FEATURED_ON&hideUpvotes=true"
              alt="Compliance Guardian on NextGen Tools" style={{ height: '48px', width: 'auto' }} />
          </a>
        </div>
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="footer-title">Compliance Guardian</div>
            <p className="footer-tagline">Continuous HIPAA compliance for Microsoft Cloud.</p>
          </div>
          <nav className="footer-nav" aria-label="Footer navigation">
            <div className="footer-column">
              <div className="footer-heading">Navigate</div>
              <ul className="footer-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><a href="/.auth/login/aad">Login</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <div className="footer-heading">Legal</div>
              <ul className="footer-links">
                <li><Link to="/privacy">Privacy</Link></li>
                <li><Link to="/terms">Terms</Link></li>
              </ul>
            </div>
            <div className="footer-column">
              <div className="footer-heading">Contact</div>
              <ul className="footer-links">
                <li><Link to="/contact">Contact Us</Link></li>
                <li><a href="mailto:support@complianceguardian.cloud">support@complianceguardian.cloud</a></li>
              </ul>
            </div>
          </nav>
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">&copy; {new Date().getFullYear()} Compliance Guardian. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;



